import { useCallback, useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Alert,
  AlertDescription,
  Input,
  Label,
} from "@repo/ui";
import { Loader2, LogIn, Building2, LogOut } from "lucide-react";
import { motion } from "framer-motion";
import { validatePassword, clearPassword, logout } from "@/lib/api/auth";
import { useAuth } from "@/lib/auth/auth-provider";
import { TwoFactorSetupDialog } from "@/components/auth/two-factor-setup-dialog";
import { SecureInput, CapsLockIndicator } from "@/components/forms";
import { useCapsLock } from "@/hooks";

export const Route = createFileRoute("/_auth/select-user")({
  component: SelectUserPage,
});

function SelectUserPage() {
  const navigate = useNavigate();
  const [initials, setInitials] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(
    null
  );
  const [show2faSetup, setShow2faSetup] = useState(false);
  const [pending2faUser, setPending2faUser] = useState<{
    id: string;
    role: string;
    full_name: string;
    initials: string;
  } | null>(null);
  const auth = useAuth();
  const { isCapsLockOn, capsLockProps } = useCapsLock();
  const pharmacyName = auth.pharmacy?.name ?? null;

  useEffect(() => {
    if (auth.isLoading) return;
    if (!auth.isAuthenticated) {
      navigate({ to: "/login" });
      return;
    }
    // If the pharmacy has no staff users yet, there is nothing to "select" —
    // route into the app so the CreateFirstUserModal can prompt for the first
    // admin user. This mirrors the guard in routes/_app.tsx and prevents a
    // fresh signup from ever landing on the staff login form.
    if (auth.needsFirstUser) {
      navigate({ to: "/dashboard" });
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.needsFirstUser, navigate]);

  useEffect(() => {
    sessionStorage.removeItem("activeUser");
  }, []);

  const handleSubmit = useCallback(async () => {
    if (initials.length < 2 || password.length < 4) return;
    setError("");
    setIsLoading(true);

    try {
      await clearPassword().catch(() => {});
      const data = (await validatePassword({
        initials: initials.toUpperCase(),
        password,
      })) as {
        success?: boolean;
        mustChangePassword?: boolean;
        requires2fa?: boolean;
        requires2faSetup?: boolean;
        user?: {
          id: string;
          role: string;
          full_name: string;
          initials: string;
        };
      };

      if (data.success) {
        if (data.mustChangePassword) {
          sessionStorage.setItem(
            "changePasswordUser",
            JSON.stringify(data.user)
          );
          navigate({ to: "/change-password" });
          return;
        }

        // 2FA required — redirect to verification page
        if (data.requires2fa) {
          navigate({ to: "/2fa-verify", search: { type: "staff" } });
          return;
        }

        // Pharmacy enforces 2FA but user hasn't set it up yet
        if (data.requires2faSetup && data.user) {
          sessionStorage.setItem("activeUser", JSON.stringify(data.user));
          await auth.refetch().catch(() => {});
          setPending2faUser(data.user);
          setShow2faSetup(true);
          return;
        }

        sessionStorage.setItem("activeUser", JSON.stringify(data.user));

        // Refresh auth context so the _app layout guard sees the active user
        // before we navigate to dashboard (otherwise it redirects back here)
        await auth.refetch();
        navigate({ to: "/dashboard" });
      }
    } catch (err) {
      const apiError = err as {
        statusCode?: number;
        error?: string;
        message?: string;
        attemptsRemaining?: number;
      };
      setError(
        apiError.error || apiError.message || "Invalid initials or password"
      );
      setAttemptsRemaining(apiError.attemptsRemaining ?? null);
      setPassword("");
    } finally {
      setIsLoading(false);
    }
  }, [initials, password, navigate, auth]);

  const handleLogout = useCallback(async () => {
    try {
      await logout();
    } catch {
      // continue to login regardless
    }
    navigate({ to: "/login" });
  }, [navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center p-4 lg:p-6 bg-gradient-to-br from-background via-background to-muted/20">
      <div className="max-w-md w-full my-4 lg:my-8">
        {/* Login Card */}
        <Card className="shadow-2xl border-0 overflow-hidden flex flex-col">
          <CardHeader className="pb-3 pt-5 lg:pb-4 lg:pt-6 flex-shrink-0">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <img
                src="/logo-with-text-blue.png"
                alt="C2 Vault"
                className="mb-4 h-6 w-auto"
              />
              {pharmacyName && (
                <div className="flex items-center gap-1.5 mb-3 text-xs text-muted-foreground">
                  <Building2 className="h-3.5 w-3.5 flex-shrink-0" />
                  <span>{pharmacyName}</span>
                </div>
              )}
              <CardTitle className="text-lg lg:text-xl font-semibold tracking-tight">
                Staff Access
              </CardTitle>
              <CardDescription className="text-xs lg:text-sm mt-1">
                Identify yourself to begin your session
              </CardDescription>
            </motion.div>
          </CardHeader>

          <CardContent className="px-6 lg:px-8 pb-6 lg:pb-8">
            <div className="space-y-3 lg:space-y-4">
              <div className="space-y-1.5">
                <Label
                  htmlFor="staff-code"
                  className="text-xs lg:text-sm font-medium"
                >
                  Initials
                </Label>
                <Input
                  id="staff-code"
                  type="text"
                  placeholder="e.g. JD"
                  value={initials}
                  onChange={(e) =>
                    setInitials(
                      e.target.value
                        .toUpperCase()
                        .replace(/[^A-Z]/g, "")
                        .slice(0, 5)
                    )
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }}
                  disabled={isLoading}
                  autoComplete="off"
                  autoFocus
                  maxLength={5}
                  className="h-9 lg:h-10 text-sm transition-all duration-200"
                  data-testid="select-user-initials-input"
                />
                <p className="text-xs text-muted-foreground">
                  Your pharmacy initials (2-5 uppercase letters)
                </p>
              </div>

              <div className="space-y-1.5">
                <Label
                  htmlFor="staff-token"
                  className="text-xs lg:text-sm font-medium"
                >
                  Password
                </Label>
                <div {...capsLockProps}>
                  <SecureInput
                    id="staff-token"
                    value={password}
                    onChange={setPassword}
                    onEnter={handleSubmit}
                    maxLength={16}
                    disabled={isLoading}
                    className="h-9 lg:h-10 text-sm transition-all duration-200"
                    data-testid="select-user-password-input"
                  />
                </div>
                <CapsLockIndicator isCapsLockOn={isCapsLockOn} />
              </div>

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Alert
                    variant="destructive"
                    className="border-destructive/50 py-2"
                    data-testid="select-user-error"
                  >
                    <AlertDescription className="text-xs lg:text-sm">
                      {error}
                      {attemptsRemaining !== null && (
                        <div className="mt-1 font-semibold">
                          {attemptsRemaining} attempt
                          {attemptsRemaining !== 1 ? "s" : ""} remaining
                        </div>
                      )}
                    </AlertDescription>
                  </Alert>
                </motion.div>
              )}

              <Button
                type="button"
                className="w-full h-9 lg:h-10 text-sm font-medium"
                disabled={
                  isLoading || initials.length < 2 || password.length < 4
                }
                onClick={handleSubmit}
                data-testid="select-user-submit"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    <LogIn className="mr-1.5 h-4 w-4" />
                    Continue
                  </>
                )}
              </Button>

              <div className="text-center">
                <Link
                  to="/forgot-staff-password"
                  search={{ initials: initials.toUpperCase() }}
                  className="text-xs text-muted-foreground hover:underline transition-colors"
                >
                  Forgot password?
                </Link>
              </div>

              <Button
                type="button"
                variant="ghost"
                className="w-full h-8 text-xs text-muted-foreground hover:text-foreground"
                onClick={handleLogout}
                disabled={isLoading}
                data-testid="select-user-logout"
              >
                <LogOut className="mr-1.5 h-3.5 w-3.5" />
                Switch pharmacy account
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
      <TwoFactorSetupDialog
        open={show2faSetup}
        enforced
        userInitials={pending2faUser?.initials ?? ""}
        onComplete={async () => {
          setShow2faSetup(false);
          await auth.refetch().catch(() => {});
          navigate({ to: "/dashboard" });
        }}
        onCancel={async () => {
          setShow2faSetup(false);
          navigate({ to: "/dashboard" });
        }}
      />
    </div>
  );
}
